import { useCallback, useRef, useState } from 'react';

const MAX_VERIFY_ATTEMPTS = 3;
/** Initial send + resends. Third code = 1 initial + 2 resends. */
const MAX_OTP_SENDS = 3;
const MAX_RESENDS = MAX_OTP_SENDS - 1;

/** Backend codes that end registration on OTP verify — not `otp_failed`. */
export const TERMINAL_OTP_ERROR_CODES = ['5002', '5003', '5004', '5005', '5006'] as const;

/**
 * Sync-safe OTP attempt / resend counters for analytics.
 * UI disable state still uses React state; analytics uses refs.
 *
 * `recordVerifyFailure` reports `attemptsExceeded` once — on the 3rd invalid
 * verify after the last allowed resend (exhausted UI).
 */
export function useOtpAnalyticsTracking() {
    const verifyAttemptRef = useRef(0);
    const resendCountRef = useRef(0);
    const attemptsExceededTrackedRef = useRef(false);
    const expiredTrackedRef = useRef(false);
    /** Landing on OTP screens means an OTP was already sent upstream. */
    const otpSentRef = useRef(true);

    const [verifyCount, setVerifyCount] = useState(0);
    const [resendCount, setResendCount] = useState(0);

    /** @returns next attempt number, or `null` if already at the limit. */
    const beginVerifyAttempt = useCallback((): number | null => {
        if (verifyAttemptRef.current >= MAX_VERIFY_ATTEMPTS) {
            return null;
        }
        return ++verifyAttemptRef.current;
    }, []);

    const recordVerifyFailure = useCallback((): {
        attempt: number;
        attemptsExceeded: boolean;
    } => {
        const attempt = verifyAttemptRef.current;
        setVerifyCount(attempt);

        const attemptsExceeded =
            attempt >= MAX_VERIFY_ATTEMPTS &&
            resendCountRef.current >= MAX_RESENDS &&
            !attemptsExceededTrackedRef.current;
        if (attemptsExceeded) {
            attemptsExceededTrackedRef.current = true;
        }

        return { attempt, attemptsExceeded };
    }, []);

    const beginResend = useCallback((): number => {
        return ++resendCountRef.current;
    }, []);

    const recordResendSuccess = useCallback((count: number): void => {
        verifyAttemptRef.current = 0;
        setVerifyCount(0);
        setResendCount(count);
        expiredTrackedRef.current = false;
        otpSentRef.current = true;
    }, []);

    const shouldTrackExpired = useCallback(
        (): boolean =>
            otpSentRef.current &&
            !expiredTrackedRef.current &&
            verifyAttemptRef.current < MAX_VERIFY_ATTEMPTS,
        []
    );

    const markExpiredTracked = useCallback((): void => {
        expiredTrackedRef.current = true;
    }, []);

    const expiredAttempt = useCallback(
        (): number => Math.max(1, verifyAttemptRef.current),
        []
    );

    return {
        verifyCount,
        resendCount,
        maxVerifyAttempts: MAX_VERIFY_ATTEMPTS,
        maxResends: MAX_RESENDS,
        beginVerifyAttempt,
        recordVerifyFailure,
        beginResend,
        recordResendSuccess,
        shouldTrackExpired,
        markExpiredTracked,
        expiredAttempt,
    };
}

/** Normalize backend codes like `error5002` / `5002`. */
export function stripErrorPrefix(code: string): string {
    return String(code ?? '').replace(/^error/i, '');
}

export function isTerminalOtpError(code: string): boolean {
    return (TERMINAL_OTP_ERROR_CODES as readonly string[]).includes(
        stripErrorPrefix(code)
    );
}
